/* STEP: Courier Details (new parent only). */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {useToast} from '../../components'
import {Field, Footer, scrollToFirstError} from './fieldKit'
import {isGst, isUrl} from '../data'
import {apiError, INTEGRATION_ENUM} from '../../supplyMeta'
import {useCreateParentMutation, useUpdateParentMutation} from '../../../../redux/supplyApiSlice'

export default function StepDetails({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const A = W.A
  const [inv, setInv] = useState({})
  const [busy, setBusy] = useState(false)
  const [createParent] = useCreateParentMutation()
  const [updateParent] = useUpdateParentMutation()
  const set = (key, value) => {
    dispatch({type: 'FIELD', slice: 'A', key, value})
    if (inv[key]) setInv((p) => ({...p, [key]: false}))
  }
  /* Supply-side onboarding is scoped to D2C — the vertical is fixed */
  useEffect(() => {
    if (A.verticals.indexOf('D2C') < 0)
      dispatch({type: 'FIELD', slice: 'A', key: 'verticals', value: ['D2C']})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onNext = async () => {
    const e = {}
    if (!A.name || A.name.trim().length < 2) e.name = true
    if (!A.legal || A.legal.trim().length < 3) e.legal = true
    if (!isGst(A.gst)) e.gst = true
    if (!A.integration) e.integration = true
    if (A.website && !isUrl(A.website)) e.website = true
    setInv(e)
    scrollToFirstError()
    if (Object.keys(e).length) return
    const payload = {
      name: A.name.trim(),
      legalEntityName: A.legal.trim(),
      verticals: ['D2C'],
      gstNumber: A.gst.trim(),
      integrationMethod: INTEGRATION_ENUM[A.integration],
      website: A.website || '',
      registeredAddress: A.address || '',
    }
    /* Guard against a stale/contaminated parentRef (e.g. a bad resumed draft) being
       silently treated as "this parent already exists, update it" — that would
       overwrite a real, unrelated parent's details. Only a parentRef we ourselves
       marked as DRAFT (created or resumed within this new-parent flow) is eligible
       for update; anything else is refused rather than guessed at. */
    if (W.parentRef && W.parentRef.id && W.parentRef.status !== 'DRAFT') {
      toast('This session\'s parent reference looks stale or invalid — go back to the Entry step and start again rather than risk overwriting the wrong parent.', 'error')
      return
    }
    setBusy(true)
    try {
      if (W.parentRef && W.parentRef.id) {
        /* already created in this session (or resumed draft) — edit-and-Next updates */
        const r = (await updateParent({id: W.parentRef.id, details: payload}).unwrap()) || {}
        const p = r.parent || r
        dispatch({type: 'SET', slice: 'parentRef', value: {id: (p && p.id) || W.parentRef.id, name: (p && p.name) || payload.name, integrationMethod: payload.integrationMethod, status: 'DRAFT'}})
        toast('Parent details updated.', 'success')
      } else {
        const r = (await createParent(payload).unwrap()) || {}
        const p = r.parent || r
        dispatch({type: 'SET', slice: 'parentRef', value: {id: p.id, name: p.name || payload.name, integrationMethod: payload.integrationMethod, status: 'DRAFT'}})
        const n = r.linkedAccountCodeCount
        toast('Parent created as draft' + (n > 0 ? ' — linked ' + n + ' existing account code' + (n > 1 ? 's' : '') : '') + '.', 'success')
      }
      goNext()
    } catch (err) {
      toast(apiError(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  const grid2 = {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px'}

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <h3 style={{margin: '0 0 16px'}}>Parent Courier Basic Details</h3>
      <div style={grid2}>
        <Field label='Parent courier name' value={A.name} onChange={(v) => set('name', v)} ph='e.g. Shadowfax' invalid={inv.name} help='Display name used across all ProShip reports. Use the brand name.' />
        <Field label='Legal entity name' value={A.legal} onChange={(v) => set('legal', v)} ph='e.g. Shadowfax Technologies Pvt Ltd' invalid={inv.legal} help='Official registered company name as per GST / ROC.' />
      </div>

      <div className='field'>
        <label>Freight vertical</label>
        <div className='row gap8' style={{display: 'flex', gap: 8}}>
          <span className='chip chip-d2c'>D2C Freight</span>
        </div>
        <div className='help' style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Supply-side onboarding is scoped to D2C Freight — the D2C commercial RBAC module governs
          this partner.
        </div>
      </div>

      <div style={grid2}>
        <Field label='GST number' value={A.gst} onChange={(v) => set('gst', v)} ph='27AABCD1234E1Z5' invalid={inv.gst} msg='Enter a valid 15-character GST number.' help='15-character GST identification number. Required for invoice processing.' />
        <Field label='Integration method' type='select' options={['Clickpost (aggregator)', 'Direct API', 'Manual (no integration)']} value={A.integration} onChange={(v) => set('integration', v)} invalid={inv.integration} help='How ProShip connects for AWB, tracking and labels.' />
      </div>
      <div style={grid2}>
        <Field label='Website' opt value={A.website} onChange={(v) => set('website', v)} ph='https://www.shadowfax.in' invalid={inv.website} msg='Website must start with https://' help='For reference only.' />
        <Field label='Registered address' opt type='textarea' value={A.address} onChange={(v) => set('address', v)} ph='Full registered office address' help='Used for correspondence and invoices.' />
      </div>

      <Footer step={W.step} disabled={busy} nextLabel={busy ? 'Saving…' : undefined} onBack={goBack} onNext={onNext} />
    </div>
  )
}

StepDetails.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
